<script setup>
import { TabPanel } from '@headlessui/vue';
import { inject, ref } from 'vue';
import CooperationModal from './CooperationModal.vue';
import CooperationCard from './CooperationCard.vue';

const programPackage = inject('programPackage');

const mainSponsor = programPackage.cooperations.mainSponsoring
    ? programPackage.cooperations.mainSponsoring[0]
    : null;

const displayedCooperation = ref(null);
</script>

<template>
    <TabPanel class="space-y-12 focus-visible:outline-deep-teal-700">
        <h2 class="text-2xl font-semibold text-deep-teal-700 -mb-6">
            {{ $t('eventDetails.tabs.cooperations') }}
        </h2>
        <div
            v-if="mainSponsor"
            class="flex flex-wrap p-8 space-y-4 lg:flex-nowrap lg:space-y-0 lg:space-x-12 bg-gray-50"
        >
            <div class="w-full space-y-4 lg:w-7/12">
                <h3 class="text-2xl font-semibold text-deep-teal-700">
                    {{ $t(`eventDetails.cooperations.displayCategory.mainSponsoring`) }}
                </h3>
                <div
                    class="prose 2xl:prose-lg max-w-none text-gray-700"
                    v-html="mainSponsor.profile.description"
                />
                <a
                    :href="mainSponsor.profile.url"
                    rel="noopener noreferrer"
                    target="_blank"
                    class="inline-flex group"
                >
                    <span class="flex items-start">
                        <span class="mr-1.5 font-semibold text-deep-teal-700 group-hover:underline">
                            {{ $t('eventDetails.cooperation.url') }}
                        </span>
                        <span class="relative flex items-center justify-center flex-shrink-0 w-6 h-6">
                            <svg
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke-width="1.5"
                                stroke="currentColor"
                                class="w-full h-full text-deep-teal-700"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                                />
                            </svg>
                        </span>
                    </span>
                </a>
            </div>
            <div class="flex lg:justify-center items-center w-full lg:w-5/12">
                <img
                    v-if="mainSponsor.profile.glide_logo_image_url_webp"
                    :title="mainSponsor.profile.company_name"
                    :alt="`${$t('eventDetails.cooperation.logo.alt')} ${mainSponsor.profile.company_name}`"
                    class="object-contain cursor-pointer"
                    :src="mainSponsor.profile.glide_logo_image_url_webp"
                    loading="lazy"
                    @click="displayedCooperation = mainSponsor"
                >
                <div
                    v-else
                    class="cursor-pointer"
                    @click="displayedCooperation = mainSponsor"
                >
                    {{ mainSponsor.profile.company_name }}
                </div>
            </div>
        </div>

        <div
            v-for="(cooperations, displayCategory) in programPackage.cooperations"
            :key="displayCategory"
            class="space-y-6"
        >
            <template v-if="displayCategory !== 'mainSponsoring'">
                <h3 class="text-2xl font-semibold text-deep-teal-700">
                    {{ $t(`eventDetails.cooperations.displayCategory.${displayCategory}`) }}
                </h3>

                <div class="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
                    <CooperationCard
                        v-for="(cooperation, index) in cooperations"
                        :key="index"
                        :cooperation
                        @click="displayedCooperation = cooperation"
                    />
                </div>
            </template>
        </div>
    </TabPanel>

    <CooperationModal
        v-if="displayedCooperation"
        :cooperation="displayedCooperation"
        :show="Boolean(displayedCooperation)"
        @closed="displayedCooperation = null"
    />
</template>
